import React, { Suspense } from 'react';
import type { ImageSize } from '../../helper/imageOptimization';
import type { BunnyImageOptions } from '../../utils/bunnyImageOptimizer';

// Lazy load the OptimizedImage component
const OptimizedImage = React.lazy(() => import('./OptimizedImage'));
const BunnyOptimizedImage = React.lazy(() => import('./BunnyOptimizedImage'));

interface LazyOptimizedImageProps {
  src: string;
  alt: string;
  size?: ImageSize;
  className?: string;
  style?: React.CSSProperties;
  lazy?: boolean;
  priority?: boolean;
  quality?: number;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
  responsive?: boolean;
  // Bunny CDN optimization options
  useBunnyOptimization?: boolean;
  bunnyOptions?: BunnyImageOptions;
  bunnyPreset?: 'thumbnail' | 'avatar' | 'hero' | 'mobile' | 'gallery' | 'square';
  bunnyPresetSize?: number;
  responsiveWidths?: number[];
}

// Loading skeleton component for OptimizedImage
const ImageSkeleton: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  size?: ImageSize;
}> = ({ className = '', style, size = 'medium' }) => {
  // Determine appropriate height based on size and className
  const getSkeletonHeight = () => {
    // Check if it's a small icon based on className
    if (className.includes('w-6 h-6') || className.includes('w-8 h-8')) {
      return '32px';
    }

    // Check for specific size patterns
    if (className.includes('w-2 h-2') || className.includes('w-3 h-3')) {
      return '12px';
    }

    // Size-based heights
    switch (size) {
      case 'small':
        return '64px';
      case 'medium':
        return '120px';
      case 'large':
        return '200px';
      default:
        return '120px';
    }
  };

  const skeletonHeight = getSkeletonHeight();
  const iconSize =
    skeletonHeight === '12px' ? 'w-2 h-2' : skeletonHeight === '32px' ? 'w-4 h-4' : 'w-8 h-8';

  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{
        width: '100%',
        height: '100%',
        minHeight: skeletonHeight,
        ...style,
      }}
    >
      <div className="flex items-center justify-center h-full">
        <svg className={`${iconSize} text-gray-400`} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
};

const LazyOptimizedImage: React.FC<LazyOptimizedImageProps> = ({
  useBunnyOptimization = false,
  bunnyOptions,
  bunnyPreset,
  bunnyPresetSize,
  responsiveWidths,
  size = 'medium',
  lazy,
  ...props
}) => {
  return (
    <Suspense
      fallback={<ImageSkeleton className={props.className} style={props.style} size={size} />}
    >
      {useBunnyOptimization ? (
        <BunnyOptimizedImage
          {...props}
          bunnyOptions={bunnyOptions}
          preset={bunnyPreset}
          presetSize={bunnyPresetSize}
          responsiveWidths={responsiveWidths}
          priority={props.priority}
          loading={lazy !== false ? 'lazy' : 'eager'}
        />
      ) : (
        <OptimizedImage {...props} size={size} lazy={lazy} />
      )}
    </Suspense>
  );
};

export default LazyOptimizedImage;
export { ImageSkeleton };
